import { CircleArrowUpFill, CircleXmarkFill } from '@gravity-ui/icons';
import { Button as HeroButton } from '@heroui/react';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField } from '@/components/InputFields';
import useFocusFirstInvalidField from '@/hooks/use-focus-first-invalid-field';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import { fieldButtonHitAreaClass } from '@/shared/components/InputFields/InputField';
import StrengthMeter from '@/shared/components/InputFields/PasswordInputField/StrengthMeter';

import HiddenIdentifierInput from './HiddenIdentifierInput';
import TogglePassword from './TogglePassword';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly onSubmit: (password: string) => Promise<void>;
  readonly errorMessage?: string;
  readonly clearErrorMessage?: () => void;
  /** Whether the password is being set in a reset password flow. */
  readonly isForgotPassword?: boolean;
};

type FieldState = {
  newPassword: string;
  confirmPassword: string;
};

const SetPassword = ({
  className,
  autoFocus,
  onSubmit,
  errorMessage,
  clearErrorMessage,
  isForgotPassword,
}: Props) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);

  const detectCapsLock = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setIsCapsLockOn(event.getModifierState('CapsLock'));
  };

  const {
    register,
    watch,
    resetField,
    clearErrors,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FieldState>({
    reValidateMode: 'onBlur',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  // Keeps RHF's onBlur revalidation while also resetting the Caps Lock hint.
  const registerWithCapsLock = (
    field: keyof FieldState,
    options?: Parameters<typeof register>[1]
  ) => {
    const { onBlur, ...rest } = register(field, options);

    return {
      ...rest,
      onKeyUp: detectCapsLock,
      onKeyDown: detectCapsLock,
      onBlur: async (event: React.FocusEvent<HTMLInputElement>) => {
        setIsCapsLockOn(false);
        await onBlur(event);
      },
    };
  };

  const formRef = useFocusFirstInvalidField(isSubmitting);

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async (data) => {
        await onSubmit(data.newPassword);
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit]
  );

  const newPassword = watch('newPassword');

  const clearButton = (field: keyof FieldState) => (
    <HeroButton
      isIconOnly
      preventFocusOnPress
      size="sm"
      variant="ghost"
      aria-label={t('action.remove')}
      className={classNames('text-muted', fieldButtonHitAreaClass)}
      onPress={() => {
        resetField(field);
      }}
    >
      <CircleXmarkFill />
    </HeroButton>
  );

  return (
    <form
      ref={formRef}
      className={classNames('flex w-full flex-col gap-4', className)}
      onSubmit={onSubmitHandler}
    >
      <HiddenIdentifierInput isForgotPassword={isForgotPassword} />
      <div className="flex flex-col gap-1">
        <InputField
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          label={t('input.password')}
          autoFocus={autoFocus}
          enterKeyHint="next"
          // A server rejection (policy, breached list, same as old) is about the new password,
          // so this field is the one marked invalid and the first to receive focus.
          isDanger={!!errors.newPassword || !!errorMessage}
          errorMessage={errors.newPassword?.message}
          {...registerWithCapsLock('newPassword', {
            required: t('error.password_required'),
            onChange: () => {
              if (errors.newPassword) {
                clearErrors('newPassword');
              }
              if (errors.confirmPassword) {
                clearErrors('confirmPassword');
              }
            },
          })}
          isSuffixFocusVisible={!!newPassword}
          suffix={clearButton('newPassword')}
        />
        {/* Always mounted: a live region that mounts with its text is often not announced.
            `absolute` (not `hidden`) while empty keeps it out of the flex gap but in the a11y tree. */}
        <div role="status" aria-live="polite" className="empty:absolute">
          {isCapsLockOn && (
            <div className="flex items-center gap-1.5 text-xs text-warning-soft-foreground animate-in fade-in duration-(--duration-quick) motion-reduce:animate-none">
              <CircleArrowUpFill className="size-3.5 shrink-0" />
              {t('input.caps_lock_on')}
            </div>
          )}
        </div>
        <StrengthMeter password={newPassword} />
      </div>

      <InputField
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        label={t('input.confirm_password')}
        isDanger={!!errors.confirmPassword}
        errorMessage={errors.confirmPassword?.message}
        {...registerWithCapsLock('confirmPassword', {
          validate: (value) => value === watch('newPassword') || t('error.passwords_do_not_match'),
          // Not revalidated per keystroke: a half-typed confirmation would flash a mismatch.
          onChange: () => {
            if (errors.confirmPassword) {
              clearErrors('confirmPassword');
            }
          },
        })}
        enterKeyHint="done"
        isSuffixFocusVisible={!!watch('confirmPassword')}
        suffix={clearButton('confirmPassword')}
      />

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <TogglePassword isChecked={showPassword} onChange={setShowPassword} />

      <Button
        name="submit"
        title="action.save_password"
        htmlType="submit"
        isLoading={isSubmitting}
        className="mt-2"
      />

      <input hidden type="submit" />
    </form>
  );
};

export default SetPassword;
